import React, { FC, useEffect, useState } from "react";
import {
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Button,
  Flex,
  message,
} from "antd";
import { useMutation } from "@tanstack/react-query";

import { addWorkingHours } from "../../../../network/workingHours";
import { queryKeys } from "../../../../services/react-query/queryKeys";
import queryClient from "../../../../services/react-query/queryClient";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { backStep } from "../../../../assets/SVGs/SVGs";
import arLocale from "antd/es/date-picker/locale/ar_EG";
import enLocale from "antd/es/date-picker/locale/en_US";
import { useIntl } from "react-intl";
import { workingHoursAndShiftResponse } from "../../../../types/teamMember";
import {
  adminAddWorkingHours,
  adminUpdateWorkingHours,
} from "../../../../network/admin";

type shiftDay = {
  day_string: string;
  from: string;
  to: string;
  off: boolean;
  breaks: {
    from: string;
    to: string;
    typeId: number;
    approved: boolean;
  }[];
  salonOff?: boolean;
};
interface AddTimeProps {
  onClose: () => void;
  onBackStep: () => void;
  team_member_id?: number;
  location_id: number;
  shiftDays: shiftDay[];
  selectedDate: string;
  config?: {
    isAdmin?: boolean;
    isEdit?: boolean;
    workingHourId?: number;
    workingHoursWithShifts?: workingHoursAndShiftResponse;
  };
}

const AddTime: FC<AddTimeProps> = ({
  onClose,
  onBackStep,
  team_member_id,
  location_id,
  shiftDays,
  selectedDate,
  config,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [endType, setEndType] = useState<"weekly" | 1 | 2 | 3 | 4 | "custom">(
    "weekly"
  );
  const { mutate: onAddWorkingHour, isLoading } = useMutation(
    (params) => {
      if (config?.isAdmin) {
        if (config?.isEdit) {
          return adminUpdateWorkingHours(
            location_id,
            team_member_id,
            config?.workingHoursWithShifts?.id,
            params
          );
        } else {
          return adminAddWorkingHours(location_id, team_member_id, params);
        }
      } else {
        if (config?.isEdit) {
          return; //! edit by salon owner request
        } else {
          return addWorkingHours(location_id, team_member_id, params);
        }
      }
    },
    {
      onSuccess: (res: any) => {
        if (
          res?.response?.status === 400 ||
          res?.response?.status === 422 ||
          res?.response?.status === 500 ||
          res?.response?.status === 404
        ) {
          message.error(res?.response?.data?.message);
        } else {
          onClose();
          message.success("Submit success!");
          // window.location.reload();
          queryClient.invalidateQueries([queryKeys.workingHours]);
          queryClient.invalidateQueries(["TeamMember"]);
        }
      },
      onError: (error) => {
        message.error("حاول مرة اخرى");
      },
    }
  );

  const schedule_type_val = Form.useWatch("schedule_type", form);
  const start_date_val = Form.useWatch("start_date", form);

  const { currentLang } = useSelector(
    (state: { user: { currentLang: "en" | "ar" } }) => state.user
  );
  useEffect(() => {
    let endDateTime;
    switch (schedule_type_val) {
      case 1:
        endDateTime = dayjs(start_date_val).add(1, "week");
        break;
      case 2:
        endDateTime = dayjs(start_date_val).add(2, "week");
        break;
      case 3:
        endDateTime = dayjs(start_date_val).add(3, "week");
        break;
      case 4:
        endDateTime = dayjs(start_date_val).add(1, "month");
        break;
      case "weekly":
        endDateTime = dayjs(start_date_val).add(3, "month");
        break;
    }
    if (schedule_type_val !== "custom") {
      form.setFieldValue("end_date", endDateTime);
    } else {
      form.setFieldValue("end_date", null);
    }
    // console.log(endDateTime, 'endDateTimeendDateTime');
  }, [schedule_type_val, start_date_val]);

  const onFinish = async (values: any) => {
    values.team_member_id = team_member_id;
    values.location_id = location_id;
    let end_date = values?.end_date?.format("YYYY-MM-DD");
    if (endType === "custom") {
      end_date = values?.end_date?.format("YYYY-MM-DD");
    } else if (+endType > 0) {
      end_date = values?.start_date
        ?.add(dayjs.duration({ days: +endType * 7 }))
        ?.format("YYYY-MM-DD");
    }
    const workingHourData = {
      working_shifts: shiftDays
        ?.filter((day) => day?.salonOff !== true)
        ?.map((shift) => ({
          ...shift,
          day: shift?.day_string,
          start_time: shift?.from,
          end_time: shift?.to,
        })),
      ...values,

      end_date,
      schedule_type: undefined,
      start_date: values["start_date"].format("YYYY-MM-DD"),
    };
    console.log(shiftDays, workingHourData);
    onAddWorkingHour(workingHourData);
  };

  return (
    <Form
      name="complex-form"
      onFinish={onFinish}
      layout="vertical"
      className="form-style relative"
      form={form}
      initialValues={
        config?.isEdit && config?.workingHoursWithShifts
          ? {
              start_date: dayjs(config?.workingHoursWithShifts?.start_date),
              end_date: dayjs(config?.workingHoursWithShifts?.end_date),
            }
          : {
              start_date: dayjs(selectedDate),
              end_date: dayjs(selectedDate),
            }
      }
    >
      <span
        className="absolute -top-10 -right-10 cursor-pointer text-color-sec-title"
        onClick={onBackStep}
      >
        {backStep}
      </span>
      <Row gutter={{ xs: 1, sm: 1, md: 8, lg: 14, xl: 14 }}>
        <Col className="gutter-row" span={24}>
          <Form.Item
            required={false}
            name={"schedule_type"}
            label={intl.formatMessage({
              id: "shiftRepeat",
              defaultMessage: "تكرار الدوره",
            })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "selectShiftRepeat",
                  defaultMessage: "اختر تكرار الدوره",
                }),
              },
            ]}
            initialValue={endType}
          >
            <Select
              defaultActiveFirstOption
              placeholder={intl.formatMessage({
                id: "shiftRepeat",
                defaultMessage: intl.formatMessage({
                  id: "selectShiftRepeat",
                  defaultMessage: "اختر تكرار الدوره",
                }),
              })}
              className="w-full my-input"
              options={[
                // { value: 'weekly', label: 'Weekly' },
                {
                  value: 1,
                  label: currentLang === "ar" ? "أسبوع واحد" : "One Week",
                },
                {
                  value: 2,
                  label: currentLang === "ar" ? "أسبوعان" : "Two Weeks",
                },
                {
                  value: 3,
                  label: currentLang === "ar" ? "ثلاثة أسابيع" : "Three Weeks",
                },
                {
                  value: 4,
                  label: currentLang === "ar" ? "أربعة أسابيع" : "Four Weeks",
                },
                {
                  value: "weekly",
                  label: currentLang === "ar" ? "تكرار اسبوعي" : "Weekly",
                },
                {
                  value: "custom",
                  label: currentLang === "ar" ? "مخصص" : "Custom",
                },
              ].map((item) => ({
                value: item.value,
                label: item.label,
              }))}
              onChange={(val) => {
                setEndType(val);
              }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <Col className="gutter-row" xl={12} lg={12} xs={24} sm={24}>
          <Form.Item
            name="start_date"
            label={intl.formatMessage({
              id: "shiftStart",
              defaultMessage: "بداية الدوره",
            })}
            className="w-full"
            required={false}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "shiftStartRequired",
                  defaultMessage: "بدايه الدوره مطلوب",
                }),
              },
            ]}
            initialValue={dayjs(selectedDate)}
          >
            <DatePicker
              popupClassName="max-w-[250px] "
              locale={currentLang === "ar" ? arLocale : enLocale}
            />
          </Form.Item>
        </Col>
        <Col className="gutter-row" xl={12} lg={12} xs={24} sm={24}>
          <Form.Item
            name={"end_date"}
            label={
              <div className="flex justify-between items-center gap-10">
                <span>
                  {intl.formatMessage({
                    id: "shiftEnd",
                    defaultMessage: "نهايه الدوره",
                  })}
                </span>
              </div>
            }
            className="w-full"
            required={false}
            rules={[
              {
                required: endType === "custom",
                message: intl.formatMessage({
                  id: "shiftEndRequired",
                  defaultMessage: "نهايه الدوره مطلوب",
                }),
              },
            ]}
          >
            <DatePicker
              locale={currentLang === "ar" ? arLocale : enLocale}
              disabled={endType !== "custom"}
              popupClassName="max-w-[250px] "
              disabledDate={(current) => {
                const selectedStartDate = form.getFieldValue(`start_date`);
                return current && current < selectedStartDate;
              }}
            />
          </Form.Item>
        </Col>
      </Row>
      <div className="flex flex-col md:flex-row gap-2">
        <Button type="primary" block htmlType="submit" loading={isLoading}>
          {intl.formatMessage({
            id: "save",
          })}
        </Button>
        <Button
          type="primary"
          className="btn-primary-light"
          htmlType="button"
          onClick={onClose}
          block
        >
          {intl.formatMessage({
            id: "cancel",
          })}
        </Button>
      </div>
    </Form>
  );
};

export default AddTime;
